/**
 * InAppBrowserBanner — bannière qui apparaît UNIQUEMENT si l'utilisateur
 * accède à l'application depuis un navigateur intégré (webview) connu pour
 * bloquer l'audio en arrière-plan : Instagram, Facebook, Messenger, TikTok,
 * LinkedIn, Snapchat, Twitter/X, Pinterest, Line.
 *
 * Lui propose d'ouvrir le site dans le navigateur natif (Chrome / Safari)
 * où l'audio fonctionnera de manière fiable. Dismissible (sessionStorage).
 */
import React, { useEffect, useState } from 'react';
import { ExternalLink, X, Volume2 } from 'lucide-react';

const DISMISS_KEY = 'hypnose_inapp_banner_dismissed_v1';

// User-Agent fragments des principaux webviews à risque.
const IN_APP_UA_PATTERNS = [
  'Instagram',
  'FBAN', 'FBAV', 'FB_IAB', 'FBIOS', // Facebook + Messenger
  'Messenger',
  'TikTok', 'BytedanceWebview', 'musical_ly',
  'LinkedInApp',
  'Snapchat',
  'Twitter', // Twitter/X in-app
  'Pinterest',
  'Line/',
];

function detectInAppBrowser() {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  for (const p of IN_APP_UA_PATTERNS) {
    if (ua.includes(p)) {
      // Renvoie un nom lisible pour personnaliser le message.
      if (p === 'Instagram') return 'Instagram';
      if (p.startsWith('FB') || p === 'Messenger') return ua.includes('Messenger') ? 'Messenger' : 'Facebook';
      if (p === 'TikTok' || p === 'BytedanceWebview' || p === 'musical_ly') return 'TikTok';
      if (p === 'LinkedInApp') return 'LinkedIn';
      if (p === 'Snapchat') return 'Snapchat';
      if (p === 'Twitter') return 'X / Twitter';
      if (p === 'Pinterest') return 'Pinterest';
      if (p === 'Line/') return 'LINE';
      return 'cette application';
    }
  }
  return null;
}

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

export default function InAppBrowserBanner() {
  const [appName, setAppName] = useState(null);
  const [platform, setPlatform] = useState('unknown');
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    setAppName(detectInAppBrowser());
    setPlatform(detectPlatform());
  }, []);

  if (!appName || dismissed) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  // Tentative de copie de l'URL pour iOS (où l'on ne peut pas ouvrir Safari
  // directement depuis un webview Instagram).
  const copyUrl = async () => {
    try {
      const url = window.location.href;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch { /* noop */ }
  };

  // Sur Android, intent:// peut ouvrir Chrome directement.
  const openInExternalBrowser = () => {
    const url = window.location.href;
    if (platform === 'android') {
      // intent:// scheme avec fallback : Chrome ouvre la page, sinon le
      // navigateur par défaut.
      const httpsUrl = url.replace(/^https?:\/\//, '');
      const intent = `intent://${httpsUrl}#Intent;scheme=https;package=com.android.chrome;end`;
      window.location.href = intent;
    } else if (platform === 'ios') {
      // iOS ne permet pas d'ouvrir Safari depuis un webview. On copie l'URL.
      copyUrl();
    }
  };

  const instructions = platform === 'ios'
    ? 'Touchez les "···" en bas à droite, puis "Ouvrir dans Safari".'
    : platform === 'android'
      ? 'Touchez les "⋮" en haut à droite, puis "Ouvrir dans le navigateur" (ou utilisez le bouton ci-dessous).'
      : 'Ouvrez ce lien dans votre navigateur.';

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9100] px-3 pt-3"
      data-testid="inapp-browser-banner"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
    >
      <div className="mx-auto max-w-md rounded-2xl bg-amber-500 text-slate-900 shadow-2xl border border-amber-300 overflow-hidden">
        <div className="px-4 py-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-slate-900/15 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Volume2 size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">
              Le son est limité dans {appName}
            </p>
            <p className="text-xs leading-snug mt-1 text-slate-900/85">
              Pour entendre les voix guidées (Inspirez / Expirez, scan corporel…), ouvrez Hypnose clé dans votre navigateur.
            </p>
            <p className="text-[11px] leading-snug mt-1.5 text-slate-900/70">
              {instructions}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {platform === 'android' && (
                <button
                  onClick={openInExternalBrowser}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-amber-200 text-xs font-semibold hover:bg-slate-800 transition"
                  data-testid="inapp-open-chrome"
                >
                  <ExternalLink size={12} /> Ouvrir dans Chrome
                </button>
              )}
              {platform === 'ios' && (
                <button
                  onClick={copyUrl}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900 text-amber-200 text-xs font-semibold hover:bg-slate-800 transition"
                  data-testid="inapp-copy-url"
                >
                  <ExternalLink size={12} /> Copier le lien
                </button>
              )}
              <button
                onClick={dismiss}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/15 text-slate-900 text-xs font-medium hover:bg-slate-900/25 transition"
                data-testid="inapp-banner-dismiss"
              >
                Continuer ici
              </button>
            </div>
          </div>
          <button
            onClick={dismiss}
            className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-900/15 hover:bg-slate-900/30 flex items-center justify-center mt-0.5"
            aria-label="Fermer"
            data-testid="inapp-banner-close"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
